import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Card,
	CardActionArea,
	CardContent,
	CardMedia,
	Typography,
	Tooltip,
	IconButton,
	SpeedDial,
	SpeedDialAction,
	Box,
	Skeleton,
	Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom"; // Import useNavigate hook
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import "./MachineCard.css";
import Swal from "sweetalert2";
import ModelButton from "../ModelButton/ModelButton";
import { ASSETS_BASE_URL, SERVER_BASE_URL, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL, EXTERNAL_2D_ENABLED } from "../../config";
import { Inventory as InventoryIcon } from "@mui/icons-material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import { useAuth } from "../../context/authContext";
import MachineNotifyForm from "../MachineNotifyForm/MachineNotifyForm";
import ContactCCRForm from "../ContactCCRForm/ContactCCRForm";

import { ALERT_MESSAGES, LABELS, MODELSOURCE, ROUTES, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { useSite } from "../../context/siteContext";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import BomViewer from "../BomViewer/BomViewer";
import { MACHINE_NODE_PREFIX } from "../../utilities/bomUtils";
import { PiTreeStructureFill } from "react-icons/pi";
import BomProductQuickView from "../BomProductQuickView/BomProductQuickView";
import { useTheme } from "@mui/material/styles";
import { processModel } from "../../services/multipleModelViewer";
import { verifyResourceExists } from "../../services/modelService";
import { countAvailableModelsFromModelData } from "../../utilities/selectBestModel";
import { Hatch } from "ldrs/react";
import "ldrs/react/Hatch.css";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import { getAssignedMachineIdsForSite } from "../../services/documentService";
import {
	buildExternalLoginUrl,
	buildExternalTargetUrl,
	getMachineDocEntry,
	openExternalDocumentation,
} from "../../utilities/externalDocs";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";
import { trackModelOpened } from "../../utilities/analyticsService";

function extractFilename(path) {
	if (!path) return "";
	const parts = String(path).split("/");
	const lastPart = parts[parts.length - 1] || "";
	return lastPart.replace(/\.[^/.]+$/, "");
}

function build2dUrl(pathValue) {
	if (!pathValue) return "";
	let normalized = String(pathValue).trim();
	if (!normalized) return "";
	if (/^https?:\/\//i.test(normalized)) return normalized;
	normalized = normalized.replace(/\\/g, "/");
	normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
	normalized = normalized.replace(/^\/?stock-models\//i, "");
	normalized = normalized.replace(/^\/?models\//i, "");
	normalized = normalized.replace(/^\/?modelfiles\//i, "");
	normalized = normalized.replace(/^\/+/i, "");
	if (!/^2d\//i.test(normalized)) {
		normalized = `2D/${normalized}`;
	}
	if (!/\.pdf$/i.test(normalized)) {
		normalized = `${normalized}.pdf`;
	}
	return `${SERVER_BASE_URL}/stock-models/${normalized}`;
}

function normalize3DFile(file) {
	if (!file) return { glb: null, html: null };
	if (typeof file === "string") return { glb: null, html: file };
	return {
		glb: file.glb || null,
		html: file.html || null,
	};
}

const MachineCard = ({
	product,
	showOnlyModelIcons = false,
	subtitleContent = null,
	forcedModel = null,
	iconsLoading = false,
}) => {
	const [open, setOpen] = useState(false);
	const [contactCcrOpen, setContactCcrOpen] = useState(false);
	const { t } = useTranslation();
	const navigate = useNavigate(); // Hook to navigate to other pages
	const location = useLocation();
	const theme = useTheme();
	const { user } = useAuth();
	const { optionKeys } = useOptions();
	const machineSellNotifyEnabled = String(optionKeys?.enableMachineSellNotifyForm ?? "false").trim().toLowerCase() === "true";
	const [has3DModel, setHas3DModel] = useState(false);
	const [has3DModelCP, setHas3DModelCP] = useState(false);
	const [has2DModel, setHas2DModel] = useState(false);
	const [hasMultipleModels, setHasMultipleModels] = useState(false);
	const [hasAssignedDocumentation, setHasAssignedDocumentation] = useState(false);
	const [loadingResources, setLoadingResources] = useState(true);
	const { selectedSite, selectedSiteCurrency, selectedSiteData } = useSite();
	const primaryColor = theme.palette.primary.main;
	const { stockImageUrl } = useBrandingAssets();
	const loaderColor = '#eee';
	const machineNodeId = useMemo(
		() => `${MACHINE_NODE_PREFIX}-${product.machine_id || "card"}`,
		[product.machine_id]
	);
	const returnPath = useMemo(
		() => `${location.pathname}${location.search}${location.hash}`,
		[location.pathname, location.search, location.hash]
	);
	const lineQuery = useMemo(() => {
		const siteLineId = product?.site_line_id;
		if (!siteLineId) return "";
		const urlLineName = new URLSearchParams(location.search).get("line_name") || "";
		const urlLineNumber = new URLSearchParams(location.search).get("line_number") || "";
		const params = new URLSearchParams();
		params.set("site_line_id", siteLineId);
		if (urlLineName || product?.line_name) params.set("line_name", urlLineName || product?.line_name || "");
		if (urlLineNumber || product?.line_number) params.set("line_number", urlLineNumber || product?.line_number || "");
		return `?${params.toString()}`;
	}, [product?.site_line_id, product?.line_name, product?.line_number, location.search]);
	const bomTreeQuery = useMemo(() => {
		const params = new URLSearchParams(lineQuery.startsWith("?") ? lineQuery.slice(1) : lineQuery);
		params.set("view", "bom_tree");
		return `?${params.toString()}`;
	}, [lineQuery]);
	const [bomModalOpen, setBomModalOpen] = useState(false);
	const [bomLoading, setBomLoading] = useState(false);
	const [bomTree, setBomTree] = useState([]);
	const [bomError, setBomError] = useState("");
	const [bomExpandedNodes, setBomExpandedNodes] = useState([]);
	const [bomSelectedNodeId, setBomSelectedNodeId] = useState(null);
	const [quickViewState, setQuickViewState] = useState({
		open: false,
		productId: null,
	});
	const normalizedMachine = useMemo(
		() => ({
			...product,
			machine_name:
				product?.machine_name || product?.name || product?.machine_product_name || "",
			machine_number:
				product?.machine_number ||
				product?.machine_serial_number ||
				product?.machine_product_name ||
				"",
		}),
		[product]
	);
	const machineNumberDisplay = useMemo(() => {
		return formatMachineNumberFromRow(product);
	}, [product]);
	const machineProductCountRaw = product?.bom_part_count;
	const hasBomCount = machineProductCountRaw !== null && machineProductCountRaw !== undefined;
	const machineProductCount = Number(machineProductCountRaw ?? 0);
	const machineHasProducts = hasBomCount ? machineProductCount > 0 : true;
	const machineSerial = product?.machine_product_name;
	const showMachineSerial =
		Boolean(machineSerial) &&
		machineSerial !== "EMPTY" &&
		machineSerial !== "N/A" &&
		machineSerial !== "null";
	const machineSerialForDocs =
		product?.machine_product_name ||
		product?.machine_serial_number ||
		product?.machine_number ||
		"";
	const siteIdForDocs = selectedSiteData?.site_id;
	const cusIdForDocs = selectedSiteData?.cus_id;
	const siteLineIdForDocs = product?.site_line_id;
	const locationIdForDocs =
		selectedSiteData?.customer_site_number ||
		selectedSiteData?.customer_site_name ||
		selectedSite ||
		"";
	const machineDocEntry = useMemo(
		() =>
			getMachineDocEntry({
				locationId: locationIdForDocs,
				serialNumber: machineSerialForDocs,
			}),
		[locationIdForDocs, machineSerialForDocs]
	);
	const shouldShowDocIcon = DOCUMENT_EXTERNAL_ENABLED
		? machineDocEntry?.HasDoc === "Y"
		: hasAssignedDocumentation;

	useEffect(() => {
		let cancelled = false;
		const machineId = Number(product?.machine_id);
		const siteId = Number(siteIdForDocs);

		if (DOCUMENT_EXTERNAL_ENABLED || !Number.isFinite(machineId) || machineId <= 0 || !Number.isFinite(siteId) || siteId <= 0) {
			setHasAssignedDocumentation(false);
			return () => {
				cancelled = true;
			};
		}

		getAssignedMachineIdsForSite(siteId, cusIdForDocs, siteLineIdForDocs)
			.then((assignedMachineIds) => {
				if (cancelled) return;
				setHasAssignedDocumentation(assignedMachineIds.has(machineId));
			})
			.catch((error) => {
				console.warn("Failed to resolve machine documentation availability:", error);
				if (cancelled) return;
				setHasAssignedDocumentation(false);
			});

		return () => {
			cancelled = true;
		};
	}, [product?.machine_id, siteIdForDocs, cusIdForDocs, siteLineIdForDocs]);

	const handleDocumentationClick = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (!DOCUMENT_EXTERNAL_ENABLED) {
			navigate(`/documents/${product.machine_id}${lineQuery}`);
			return;
		}
		if (!machineDocEntry || machineDocEntry.HasDoc !== "Y") return;
		const username = selectedSiteData?.amf_method_username_ln;
		const password = selectedSiteData?.amf_method_password_ln;
		const loginUrl = buildExternalLoginUrl({
			templateUrl: DOCUMENT_EXTERNAL_URL,
			username,
			password,
		});
		const targetUrl = buildExternalTargetUrl({
			username,
			group: machineDocEntry.Group,
			machine: machineSerialForDocs,
		});
		if (!loginUrl || !targetUrl) {
			navigate(`/documents/${product.machine_id}${lineQuery}`);
			return;
		}
		const targetWindow = window.open("about:blank", "_blank");
		openExternalDocumentation({ loginUrl, targetUrl, targetWindow });
	};

	const default_img = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;

	// 🔸 Unified machine image resolver: uses model_data.images OR model_data.all_images
	const getMachineImageUrl = () => {
		const md = product?.model_data || {};
		const img =
			(Array.isArray(md.images) && md.images[0]) ||
			(Array.isArray(md.all_images) && md.all_images[0]);

		if (!img && product?.disableMachineImageFallback) return default_img;
		if (!img) return default_img;

		// Already absolute url
		if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) {
			return img;
		}

		// Relative asset path like "MachineImages/1000-1004.png"
		return `${ASSETS_BASE_URL}/${String(img).replace(/^\/+/, "")}`;
	};

	const productImageUrl = getMachineImageUrl();

	// Parse cached model data
	useEffect(() => {
		if (iconsLoading) {
			setLoadingResources(true);
			setHas3DModel(false);
			setHas3DModelCP(false);
			setHas2DModel(false);
			setHasMultipleModels(false);
			return;
		}
		setLoadingResources(true);
		try {
			const modelData = product.model_data || {};
			const main3d = normalize3DFile(modelData["3dfile"]);
			// Check for direct machine models
			const has3D = Boolean(main3d.glb || main3d.html || modelData.splitfiles?.length > 0);
			const has3DCP = !!modelData["3d_cp"];
			const has2D = !!modelData["2dfile"];

			// Check for BOM children with models
			const bomModels = modelData.boms?.filter(bom =>
				Boolean(normalize3DFile(bom["3dfile"]).glb || normalize3DFile(bom["3dfile"]).html || bom["2dfile"])
			) || [];

			// Check if any BOM children have 2D or 3D files
			const hasBOM2D = bomModels.some(bom => bom["2dfile"]);
			const hasBOM3D = bomModels.some((bom) => {
				const threeD = normalize3DFile(bom["3dfile"]);
				return Boolean(threeD.glb || threeD.html);
			});
			// Machine has models if it has direct models OR BOM children with models
			const hasMachine2D = has2D || hasBOM2D;
			const hasMachine3D = has3D || hasBOM3D;
			const modelCount = countAvailableModelsFromModelData(modelData);
			const hasMultiple = modelCount > 1;

			setHas3DModel(hasMachine3D);
			setHas3DModelCP(has3DCP);
			setHas2DModel(hasMachine2D);
			setHasMultipleModels(hasMultiple);
		} catch (error) {
			console.warn("Error parsing model data:", error);
			setHas3DModel(false);
			setHas3DModelCP(false);
			setHas2DModel(false);
			setHasMultipleModels(false);
		} finally {
			setLoadingResources(false);
		}
	}, [product.model_data, iconsLoading]);

	useEffect(() => {
		setBomTree([]);
		setBomSelectedNodeId(null);
		setBomExpandedNodes([machineNodeId]);
	}, [machineNodeId, selectedSite]);

	useEffect(() => {
		if (!bomModalOpen) {
			setBomSelectedNodeId(null);
		}
	}, [bomModalOpen]);

	const handleCardClick = () => {
		// if (!machineHasProducts) {
		// 	setContactCcrOpen(true);
		// 	return;
		// }
		if (product.machine_id) {
			navigate(`/machines/${product.machine_id}${lineQuery}`);
			return;
		}
		Swal.fire({
			icon: "warning",
			title: t('ALERT_MESSAGES.WARNING_TITLE'),
			text: t('ALERT_MESSAGES.MACHINE_NOT_FOUND'),
		});
	};

	const handleOpenBomModal = (event) => {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (product.machine_id) {
			navigate(`/machines/${product.machine_id}${bomTreeQuery}`);
			return;
		}
		handleCardClick();
	};

	const handleNavigateToBomProduct = (productId) => {
		if (!productId) return;
		setBomModalOpen(false);
		navigate(`/machines/${product.machine_id}/${productId}${lineQuery}`);
	};

	const handleBomProductClick = (node) => {
		if (!node?.productId) return;
		setQuickViewState({
			open: true,
			productId: node.productId,
		});
	};

	const bomModelAvailability = useMemo(() => {
		const map = new Map();
		const modelData = product?.model_data || {};
		const registerEntry = (value, type, availability) => {
			if (value === undefined || value === null) return;
			const normalized = String(value).trim();
			if (!normalized) return;
			const key = `${type}:${normalized.toUpperCase()}`;
			if (!map.has(key)) {
				map.set(key, availability);
			}
		};
		const bomModels = modelData.boms || [];
		bomModels.forEach((item) => {
			const threeD = normalize3DFile(item["3dfile"]);
			const has3D = Boolean(threeD.glb || threeD.html);
			const has2D = Boolean(item["2dfile"]);
			if (!has3D && !has2D) return;
			const productNumber =
				item?.product_number || item?.productNumber || item?.filename || "";
			const availability = {
				has3D,
				has2D,
				file3d: item["3dfile"] || "",
				file2d: item["2dfile"] || "",
				filename: productNumber,
				productNumbers: productNumber ? [productNumber] : [],
				raw: item,
			};
			registerEntry(productNumber, "pn", availability);
			registerEntry(item?.product_id, "id", availability);
			registerEntry(item?.productId, "id", availability);
		});
		return map;
	}, [product?.model_data]);

	const showBomModelError = useCallback(() => {
		Swal.fire({
			icon: "error",
			title: t("ALERT_MESSAGES.ERROR"),
			text: t("ALERT_MESSAGES.MODEL_OPEN_FAILED"),
		});
	}, [t]);

	const openBomModelUrl = useCallback(async (url) => {
		if (!url) return false;
		const exists = await verifyResourceExists(url);
		if (!exists) {
			return false;
		}
		window.open(url);
		return true;
	}, []);

	const handleBomModelIconClick = useCallback(
		async ({ node, modelInfo, type }) => {
			if (!node || !modelInfo) return;
			try {
				const raw = modelInfo.raw || {};
				const threeD = raw["3dfile"];
				let glbPath = null;
				let htmlPath = null;

				if (threeD) {
					if (typeof threeD === "string") {
						htmlPath = threeD;
					} else {
						glbPath = threeD.glb || null;
						htmlPath = threeD.html || null;
					}
				}

				if (type === "2d") {
					if (!modelInfo.file2d) {
						showBomModelError();
						return;
					}
					if (EXTERNAL_2D_ENABLED) {
						const pdfUrl = build2dUrl(modelInfo.file2d);
						const opened = await openBomModelUrl(pdfUrl);
						if (!opened) {
							showBomModelError();
						} else {
							trackModelOpened("2D", {
								machine_id: product.machine_id,
								machine_name: product.machine_name,
								machine_number: product.machine_number,
								model_file: extractFilename(modelInfo.file2d),
								model_format: "pdf",
								model_source: MODELSOURCE.MACHINE_CARD_BOM,
								product_id: node.productId || node.product_id || "",
								product_number: node.productNumber || node.product_number || "",
							});
						}
					} else {
						const filename = extractFilename(modelInfo.file2d);
						if (!filename) {
							showBomModelError();
							return;
						}
						trackModelOpened("2D", {
							machine_id: product.machine_id,
							machine_name: product.machine_name,
							machine_number: product.machine_number,
							model_file: filename,
							model_format: "pdf",
							model_source: MODELSOURCE.MACHINE_CARD_BOM,
							product_id: node.productId || node.product_id || "",
							product_number: node.productNumber || node.product_number || "",
						});
						navigate(`/2d-model-viewer/${product.machine_id}/${filename}`, {
							state: { fromPath: returnPath },
						});
					}
					return;
				}

				// For 3D icon click, prefer GLB then HTML.
				if (glbPath) {
					const filename =
						extractFilename(glbPath) ||
						modelInfo.filename ||
						node.productNumber ||
						node.product_number ||
						raw.product_number;
					if (!filename) {
						showBomModelError();
						return;
					}
					trackModelOpened("3D", {
						machine_id: product.machine_id,
						machine_name: product.machine_name,
						machine_number: product.machine_number,
						model_file: filename,
						model_format: "glb",
						model_source: MODELSOURCE.MACHINE_CARD_BOM,
						product_id: node.productId || node.product_id || "",
						product_number: node.productNumber || node.product_number || "",
					});
					navigate(`/model-viewer/${product.machine_id}/${filename}`, {
						state: { fromPath: returnPath },
					});
					return;
				}

				if (htmlPath) {
					const filename =
						extractFilename(htmlPath) ||
						modelInfo.filename ||
						node.productNumber ||
						node.product_number ||
						raw.product_number;
					if (!filename) {
						showBomModelError();
						return;
					}
					const productNumbers =
						(modelInfo.productNumbers && modelInfo.productNumbers.length > 0
							? modelInfo.productNumbers
							: [node.productNumber || node.product_number || filename].filter(Boolean)) || [];
					const response = await processModel(
						"/models/process-modelfile",
						filename,
						product.machine_name,
						(selectedSiteCurrency || "USD").toUpperCase(),
						user?.user_id,
						productNumbers,
						product.machine_id,
						primaryColor,
						selectedSite,
						product.machine_number
					);
					if (response.status === 200 && response.data?.url) {
						const fullUrl = `${SERVER_BASE_URL}${response.data.url}`;
						const opened = await openBomModelUrl(fullUrl);
						if (!opened) {
							showBomModelError();
						} else {
							trackModelOpened("3D", {
								machine_id: product.machine_id,
								machine_name: product.machine_name,
								machine_number: product.machine_number,
								model_file: filename,
								model_format: "html",
								model_source: MODELSOURCE.MACHINE_CARD_BOM,
								product_id: node.productId || node.product_id || "",
								product_number: node.productNumber || node.product_number || "",
							});
						}
					} else {
						showBomModelError();
					}
					return;
				}

				showBomModelError();
			} catch (error) {
				console.error("Failed to open BOM model", error);
				showBomModelError();
			}
		},
		[
			navigate,
			openBomModelUrl,
			product.machine_id,
			product.machine_name,
			product.machine_number,
			selectedSite,
			selectedSiteCurrency,
			showBomModelError,
			primaryColor,
			user?.user_id,
		]
	);

	return (
		<>
			<Card className="machine-card" data-tour='product-3d-model'>
				<CardActionArea className="machine-card-content" onClick={handleCardClick}>
					<CardMedia
						component="img"
						image={productImageUrl}
						alt={product.machine_name}
						loading="lazy"
						className="machine-img"
						onError={(e) => {
							e.target.src = default_img;
						}}
					/>
					<CardContent sx={{ paddingBottom: "10px", textAlign: "center" }} >
						<Tooltip title={product.machine_name}>
							<Typography
								variant="body1"
								sx={{
									fontWeight: "bold",
									lineHeight: "1.2rem",
									marginBottom: "5px",
									display: "-webkit-box",
									overflow: "hidden",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									textOverflow: "ellipsis",
									minHeight: "2.4rem",
									color: "#000",
								}}>
								{product.machine_name}
							</Typography>
						</Tooltip>
						{subtitleContent ? (<>
							<Box sx={{ mt: 0.5 }}>
								{subtitleContent}
							</Box>
						</>) : (<>

						{showMachineSerial ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
								<b>{t('LABELS.MACHINE_SERIAL')}</b>{" "}
								{machineSerial}
							</Typography>
						) : null}
							<Tooltip title={machineNumberDisplay || ""}>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									fontSize: "11px",
									display: "-webkit-box",
									overflow: "hidden",
									WebkitLineClamp: 1,
									WebkitBoxOrient: "vertical",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
									<b>{t('UI_TEXT.MACHINE')}</b> {machineNumberDisplay}
								</Typography>
							</Tooltip>
						</>)}

						<Box>
							<div className="action-buttons">
								{iconsLoading || loadingResources ? (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											minHeight: "40px",
											width: "100%",
										}}
									>
										<Hatch size="20" stroke="3" speed="3.5" color={loaderColor} />
									</Box>
								) : (
										<>
											{(has3DModel || has2DModel) && (
												<ModelButton
													machineName={product.analytics_machine_name || product.machine_name}
													machineNumber={product.machine_number}
													machineProductName={product.machine_product_name}
													machineid={product.machine_id}
													productNumbers={product.product_numbers}
													changeParts={has3DModelCP}
													has2D={has2DModel}
													hasMultipleModels={hasMultipleModels}
													modelData={product.model_data || {}}
												forcedModel={product.forcedModel || forcedModel || null}
												/>
											)}
											{!showOnlyModelIcons && (
												<>
													{shouldShowDocIcon && (
														<Tooltip title={t('TITLES.VIEW_DOCUMENTATION')}>
															<IconButton
																aria-label="Documentation"
																size="large"
																onClick={handleDocumentationClick}>
																<DescriptionOutlinedIcon />
															</IconButton>
														</Tooltip>
													)}
													{product?.has_quote_history !== false && (
													<Tooltip title={t('TITLES.VIEW_ORDER_HISTORY')}>
														<IconButton
															aria-label="Order History"
															size="large"
																onClick={(e) => {
																e.stopPropagation();
																navigate(`/order-history/${product.machine_id}${lineQuery}`);
															}}>
														    <InventoryIcon />
														</IconButton>
													</Tooltip>
													)}
													{machineHasProducts ? (
														<Tooltip title={t('UI_TEXT.VIEW_BOM')}>
															<IconButton
																aria-label="View BOM"
																size="large"
																onClick={handleOpenBomModal}
															>
																<PiTreeStructureFill size={20} />
															</IconButton>
														</Tooltip>
													) : null}
											</>
										)}
									</>
								)}
							</div>
						</Box>
					</CardContent>
					{!showOnlyModelIcons && machineSellNotifyEnabled && (<SpeedDial
						ariaLabel="Product actions"
						icon={<MoreHorizIcon variant="Outlined" sx={{ color: "primary.main", fontSize: 24 }} />}
						direction="down"
						className="speed-dial-custom"
						sx={{
							width: "10px",
							height: "10px",
							marginTop: -1,
							marginRight: 0,
							boxShadow: "none",
							backgroundColor: "transparent",
							"& .MuiFab-primary": {
								backgroundColor: "transparent",
								boxShadow: "none",
								"&:hover": {
									backgroundColor: "transparent",
								},
							},
						}}>
						<SpeedDialAction
							icon={<ContactPhoneIcon />}
							tooltipTitle={t('TITLES.MACHINE_SOLD_NOTIFY')}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setOpen(true);
							}}
						/>
					</SpeedDial>)}
				</CardActionArea>
			</Card>
			{machineSellNotifyEnabled && (
				<MachineNotifyForm
					open={open}
					setOpen={setOpen}
					machine_number={product.machine_number}
					machine_name={product.machine_name}
				/>
			)}
			{/* <ContactCCRForm open={contactCcrOpen} setOpen={setContactCcrOpen} machine={normalizedMachine} /> */}
			<ModalWrapper
				open={bomModalOpen}
				setOpen={setBomModalOpen}
				title={t('UI_TEXT.BOM_TREE_MODAL_TITLE')}
				subtitle={t('UI_TEXT.BOM_TREE_MODAL_SUBTITLE')}
				width="60vw"
				content={
					bomLoading ? (
						<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
							{Array.from({ length: 8 }).map((_, idx) => (
								<Skeleton key={idx} variant="rounded" height={28} />
							))}
						</Box>
					) : bomError ? (
						<Alert severity="error">{bomError}</Alert>
					) : (
						<BomViewer
							title={t('UI_TEXT.BOM_TREE_TITLE')}
							helperText={t('UI_TEXT.BOM_TREE_HELPER')}
							clearSelectionLabel={t('UI_TEXT.CLEAR_BOM_SELECTION')}
							noDataLabel={t('UI_TEXT.NO_BOM_DATA')}
							machineName={product.machine_name}
							machineNumber={product.machine_number}
							machineNodeId={machineNodeId}
							treeData={bomTree}
							selectedNodeId={bomSelectedNodeId}
							expandedNodes={bomExpandedNodes}
							checkedProductIds={[]}
							onNodeSelect={setBomSelectedNodeId}
							onClearSelection={() => setBomSelectedNodeId(null)}
							onToggle={setBomExpandedNodes}
							onProductClick={handleBomProductClick}
							variant="modal"
							isLoading={bomLoading}
							bomSearchPlaceholder={t('UI_TEXT.BOM_TREE_SEARCH_PLACEHOLDER')}
							showCheckboxes={false}
							onNavigateToProduct={handleNavigateToBomProduct}
							modelAvailability={bomModelAvailability}
							onModelIconClick={handleBomModelIconClick}
							machineHasDocumentation={shouldShowDocIcon}
							onMachineDocumentationClick={handleDocumentationClick}
						/>
					)
				}
			/>
			<BomProductQuickView
				open={quickViewState.open}
				productId={quickViewState.productId}
				machineId={product.machine_id}
				machineName={product.machine_name}
				onClose={() => setQuickViewState({ open: false, productId: null })}
			/>
		</>
	);
};

export default MachineCard;
